// src/components/pos/PendingOrdersDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, ShoppingBag, RotateCcw, Trash2 } from 'lucide-react';
import { saleApi } from '../../api';

const PendingOrdersDialog = ({ onClose, onRestore }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPending();
  }, []);

  const loadPending = async () => {
    setLoading(true);
    try {
      const res = await saleApi.getPending();
      if (res.success) setOrders(res.data || []);
    } catch (err) {
      console.error('加载挂单失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (order) => {
    onRestore(order);
    onClose();
  };

  const handleCancel = async (order_no) => {
    if (!window.confirm('确定取消这个挂单吗？')) return;
    try {
      const res = await saleApi.cancelSale(order_no);
      if (res.success) {
        setOrders(prev => prev.filter(o => o.order_no !== order_no));
      } else {
        alert(res.message || '取消失败');
      }
    } catch (err) {
      alert('取消失败');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">

        {/* 头部 */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-bold">挂单列表</h3>
            {orders.length > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                {orders.length} 单
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShoppingBag className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p>暂无挂单</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div key={order.order_no}
                  className="border border-amber-200 rounded-xl p-4 bg-amber-50 hover:shadow-md transition-shadow">

                  {/* 订单头部 */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-bold text-gray-800">{order.order_no}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {new Date(order.created_at).toLocaleString('zh-CN')}
                        {order.cashier_name && ` · ${order.cashier_name}`}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-amber-600">
                      ${parseFloat(order.total_amount).toFixed(2)}
                    </p>
                  </div>

                  {/* 商品列表 */}
                  <div className="space-y-1 mb-3">
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-gray-600">
                        <span className="truncate flex-1">{item.product_name}</span>
                        <span className="ml-2 flex-shrink-0">
                          × {item.quantity} = ${parseFloat(item.subtotal).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-xs text-gray-400">
                        还有 {order.items.length - 3} 件商品...
                      </p>
                    )}
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRestore(order)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors">
                      <RotateCcw className="w-4 h-4" />
                      取出结算
                    </button>
                    <button
                      onClick={() => handleCancel(order.order_no)}
                      className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingOrdersDialog;